<script setup>
    import { computed, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import moment from 'moment'

    import { IconBotSparkle20Regular, IconThumbLike20Filled, IconThumbLike20Regular, IconThumbDislike20Filled, IconThumbDislike20Regular } from '@iconify-prerendered/vue-fluent'
    import Loader from '@/Components/App/Loader.vue'

    import { useNotesStore } from '@/Stores'

    const props = defineProps({
        subject: {
            type: Object,
            required: true,
        },
        events: {
            type: Array,
            default: () => [],
        },
    })

    const emit = defineEmits(['navigate'])

    const THUMBS = [
        { rating: 'up', title: 'This summary was helpful', colorClass: 'text-lifeworx-blue-600', hoverClass: 'hover:text-lifeworx-blue-600', filled: IconThumbLike20Filled, regular: IconThumbLike20Regular },
        { rating: 'down', title: 'This summary was wrong or missing something', colorClass: 'text-lifeworx-red-500', hoverClass: 'hover:text-lifeworx-red-500', filled: IconThumbDislike20Filled, regular: IconThumbDislike20Regular },
    ]

    const notesStore = useNotesStore()
    const { summary, pending, generating, failed, loadingSummary } = storeToRefs(notesStore)

    const commentOpen = ref(false)
    const comment = ref('')
    const submittingFeedback = ref(false)
    const feedbackThanks = ref(false)

    /**
     * A summary, once loaded, always stays visible; the pending state is shown alongside it rather than replacing it.
     */
    const view = computed(() => {
        if (summary.value) {
            return 'ready'
        }
        if (pending.value) {
            return 'generating'
        }

        return loadingSummary.value ? 'loading' : 'empty'
    })

    /**
     * The header shows the pending state for both a first summary and an update to an existing one.
     */
    const pendingLabel = computed(() => {
        if (view.value === 'generating') {
            return 'Generating summary…'
        }

        return generating.value ? 'Generating…' : 'Updates pending'
    })

    /**
     * Orange while the pending update is generating, grey once its last run failed, blue while it waits for a worker.
     */
    const spinnerColorClass = computed(() => {
        if (generating.value) {
            return 'border-lifeworx-orange-500'
        }

        return failed.value ? 'border-zinc-400' : 'border-lifeworx-blue-400'
    })

    const eventKeys = computed(() => new Set(props.events.map((event) => event.key)))

    const sections = computed(() => summary.value?.summary?.sections ?? [])

    const myFeedback = computed(() => summary.value?.my_feedback?.[0] ?? null)

    const generatedAt = computed(() => {
        const timestamp = summary.value?.generated_at
        return timestamp ? moment(timestamp).fromNow() : ''
    })

    watch(
        () => summary.value?.id,
        () => {
            closeComment()
            feedbackThanks.value = false
        },
    )

    function closeComment() {
        commentOpen.value = false
        comment.value = ''
    }

    function thank() {
        closeComment()
        feedbackThanks.value = true
    }

    async function sendFeedback(feedback, onSaved) {
        if (!summary.value || submittingFeedback.value) {
            return
        }

        submittingFeedback.value = true
        feedbackThanks.value = false
        try {
            await notesStore.submitFeedback(summary.value.id, feedback)
            onSaved()
        } catch (e) {
            // Error notification is handled by the store.
        } finally {
            submittingFeedback.value = false
        }
    }

    function rate(rating) {
        sendFeedback({ rating }, rating === 'down' ? () => (commentOpen.value = true) : thank)
    }

    function submitComment() {
        sendFeedback({ rating: 'down', comment: comment.value.trim() || null }, thank)
    }
</script>

<template>
    <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-1.5">
                <span class="relative flex size-6 shrink-0 items-center justify-center">
                    <span
                        v-if="pending"
                        data-test="summary-pending-spinner"
                        :class="spinnerColorClass"
                        class="absolute inset-0 animate-spin rounded-full border-2 border-t-transparent"
                        aria-hidden="true"
                    />
                    <IconBotSparkle20Regular
                        class="size-4 text-lifeworx-blue-600"
                        aria-hidden="true"
                    />
                </span>
                <span class="text-xs font-semibold uppercase tracking-wider text-zinc-500">Summary</span>
                <span
                    v-if="pending"
                    data-test="summary-pending"
                    class="text-xs italic text-zinc-500"
                >
                    {{ pendingLabel }}
                </span>
            </div>

            <div
                v-if="view === 'ready'"
                class="flex items-center gap-1"
            >
                <button
                    v-for="thumb in THUMBS"
                    :key="thumb.rating"
                    type="button"
                    class="rounded-full p-1 text-zinc-500 transition-colors hover:bg-zinc-100 disabled:opacity-50"
                    :class="[thumb.hoverClass, { [thumb.colorClass]: myFeedback?.rating === thumb.rating }]"
                    :disabled="submittingFeedback"
                    :aria-pressed="myFeedback?.rating === thumb.rating"
                    :title="thumb.title"
                    @click="rate(thumb.rating)"
                >
                    <component
                        :is="myFeedback?.rating === thumb.rating ? thumb.filled : thumb.regular"
                        class="size-5"
                    />
                </button>
            </div>
        </div>

        <div
            v-if="view === 'loading'"
            class="py-6"
        >
            <Loader />
        </div>

        <p
            v-else-if="view === 'empty'"
            data-test="summary-empty"
            class="text-sm text-zinc-500"
        >
            No summary available yet.
        </p>

        <template v-else-if="view === 'ready'">
            <div
                v-if="commentOpen"
                class="flex flex-col gap-2"
            >
                <textarea
                    v-model="comment"
                    data-test="summary-feedback-comment"
                    rows="3"
                    class="h-20 w-full resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm placeholder:italic placeholder:text-stone-400 focus:border-lifeworx-blue-600 focus:outline-none focus:ring-0"
                    placeholder="What was wrong or missing? Your feedback helps improve these summaries."
                />
                <div class="flex justify-end gap-2">
                    <button
                        type="button"
                        class="rounded-md px-3 py-1 text-xs font-semibold text-zinc-600 hover:bg-zinc-100"
                        :disabled="submittingFeedback"
                        @click="closeComment()"
                    >
                        Skip
                    </button>
                    <button
                        type="button"
                        class="rounded-md bg-lifeworx-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-lifeworx-blue-700 disabled:opacity-50"
                        :disabled="submittingFeedback"
                        @click="submitComment"
                    >
                        Submit
                    </button>
                </div>
            </div>

            <p
                v-else-if="feedbackThanks"
                class="text-xs text-lifeworx-green-600"
            >
                Thanks for the feedback!
            </p>

            <div class="flex min-h-0 flex-col gap-3 text-sm leading-relaxed text-lifeworx-blue-800">
                <div
                    v-for="(section, sectionIndex) in sections"
                    :key="sectionIndex"
                >
                    <p class="whitespace-pre-wrap">
                        <template
                            v-for="(segment, segmentIndex) in section.segments"
                            :key="segmentIndex"
                        >
                            <a
                                v-if="segment.event_ref && eventKeys.has(segment.event_ref)"
                                href="#"
                                role="button"
                                class="inline cursor-pointer font-bold text-lifeworx-blue-600 underline hover:text-lifeworx-blue-700"
                                @click.prevent="emit('navigate', segment.event_ref)"
                                @keydown.space.prevent="emit('navigate', segment.event_ref)"
                                v-text="segment.text"
                            />
                            <span
                                v-else
                                v-text="segment.text"
                            />
                        </template>
                    </p>
                </div>
            </div>

            <div class="flex flex-wrap items-center gap-x-1 border-t border-zinc-100 pt-2 text-xs text-zinc-400">
                <span data-test="summary-generated-at">Generated {{ generatedAt }}</span>
            </div>
        </template>
    </div>
</template>
